import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { useExchangeRate } from "@/features/regions/hooks/use-exchange-rate";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";
import { getBundlesGrouped } from "@/features/bundles/services/get-bundles-grouped";

type Mode = "INCREASE" | "DECREASE";
type ValueType = "AMOUNT" | "PERCENT";

function adjust(base: number, mode: Mode, valueType: ValueType, value: number) {
  if (Number.isNaN(value)) return base;
  const sign = mode === "DECREASE" ? -1 : 1;
  if (valueType === "PERCENT") return Math.round(base * (1 + (sign * value) / 100));
  return Math.round(base + sign * value);
}

// Shared clone step (fixed target): pick a source region, preview which products
// clone vs already exist, and set the final per-offering price — converted into
// the target currency (Frankfurter), bulk-adjustable, and editable per row. The
// parent owns the clone action; prices flow up via `onPricesChange`.
export default function RegionCloneStep({
  targetRegionId,
  sourceId,
  onSourceChange,
  onPricesChange,
  onSelectionChange,
  onMeta,
}: {
  targetRegionId: string;
  sourceId: string;
  onSourceChange: (id: string) => void;
  // final amounts keyed by SOURCE standard_id (target currency)
  onPricesChange?: (overrides: Record<string, number>) => void;
  // the product ids the admin chose to clone (will-clone minus deselected)
  onSelectionChange?: (productIds: string[]) => void;
  onMeta?: (info: { toCloneCount: number }) => void;
}) {
  const { data: regions } = useActiveRegions();

  useEffect(() => {
    if (!sourceId && regions?.length) {
      const fallback = regions.find(
        (r) => r.is_fallback && r.id !== targetRegionId,
      );
      if (fallback) onSourceChange(fallback.id);
    }
  }, [regions, sourceId, targetRegionId, onSourceChange]);

  const source = regions?.find((r) => r.id === sourceId);
  const target = regions?.find((r) => r.id === targetRegionId);

  const { data: sourceGrouped } = useQuery({
    queryKey: ["bundles", "grouped", { region: source?.code }],
    queryFn: () => getBundlesGrouped({ region: source!.code }),
    enabled: !!source,
  });
  const { data: targetGrouped } = useQuery({
    queryKey: ["bundles", "grouped", { region: target?.code }],
    queryFn: () => getBundlesGrouped({ region: target!.code }),
    enabled: !!target,
  });

  const rate = useExchangeRate(source?.currency, target?.currency);
  // Manual rate fallback when the API fails.
  const [manualRate, setManualRate] = useState("");
  const apiRate = rate.isError ? null : (rate.data ?? null); // number | null | undefined(loading)
  const manualRateNum = (() => {
    const n = parseFloat(manualRate);
    return !Number.isNaN(n) && n > 0 ? n : null;
  })();
  // Effective rate: API rate when available, else the manually-entered one.
  const rateValue = apiRate ?? manualRateNum;

  const targetProductIds = useMemo(
    () =>
      new Set(
        (targetGrouped?.groups ?? [])
          .filter((g) => g.product && g.offerings.some((o) => o.current))
          .map((g) => g.product!.id),
      ),
    [targetGrouped],
  );

  // Products → current offerings; skip flag is product-level.
  const products = useMemo(
    () =>
      (sourceGrouped?.groups ?? [])
        .filter((g) => g.product && g.offerings.some((o) => o.current))
        .map((g) => ({
          productId: g.product!.id,
          product: g.product!.name,
          skip: targetProductIds.has(g.product!.id),
          offerings: g.offerings
            .filter((o) => o.current)
            .map((o) => ({
              standardId: o.standard_id,
              name: o.name,
              sourceAmount: o.current!.amount,
              sourceCurrency: o.current!.currency,
            })),
        })),
    [sourceGrouped, targetProductIds],
  );

  const cloneRows = useMemo(
    () => products.filter((p) => !p.skip).flatMap((p) => p.offerings),
    [products],
  );
  const rowsKey = cloneRows.map((r) => r.standardId).join(",");

  // Per-product selection — every "will clone" product is selected by default;
  // the admin can deselect any they don't want cloned into the target.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const toggleProduct = (productId: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  const includedProducts = useMemo(
    () => products.filter((p) => !p.skip && !excluded.has(p.productId)),
    [products, excluded],
  );
  const includedRows = useMemo(
    () => includedProducts.flatMap((p) => p.offerings),
    [includedProducts],
  );

  const convertedBase = (sourceAmount: number) =>
    rateValue != null ? Math.round(sourceAmount * rateValue) : sourceAmount;

  // Final per-offering price (target currency), editable.
  const [expected, setExpected] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<Mode>("INCREASE");
  const [valueType, setValueType] = useState<ValueType>("PERCENT");
  const [value, setValue] = useState("");

  // Reset to the converted base whenever the source / rate / row set changes.
  useEffect(() => {
    setExpected(
      Object.fromEntries(
        cloneRows.map((r) => [r.standardId, String(convertedBase(r.sourceAmount))]),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, rateValue, rowsKey]);

  const applyToAll = () => {
    const v = parseFloat(value);
    if (Number.isNaN(v)) return;
    setExpected(
      Object.fromEntries(
        cloneRows.map((r) => [
          r.standardId,
          String(adjust(convertedBase(r.sourceAmount), mode, valueType, v)),
        ]),
      ),
    );
  };

  // Push final overrides (selected products only) up. Ref avoids re-running on
  // parent callback identity.
  const onPricesRef = useRef(onPricesChange);
  onPricesRef.current = onPricesChange;
  useEffect(() => {
    const overrides: Record<string, number> = {};
    for (const r of includedRows) {
      const v = Math.round(parseFloat(expected[r.standardId]));
      if (!Number.isNaN(v) && v >= 0) overrides[r.standardId] = v;
    }
    onPricesRef.current?.(overrides);
  }, [expected, includedRows]);

  // Report the selected product ids up.
  const onSelectionRef = useRef(onSelectionChange);
  onSelectionRef.current = onSelectionChange;
  const includedProductIds = includedProducts.map((p) => p.productId);
  const selectionKey = includedProductIds.join(",");
  useEffect(() => {
    onSelectionRef.current?.(includedProductIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  const toCloneCount = includedRows.length;
  const onMetaRef = useRef(onMeta);
  onMetaRef.current = onMeta;
  useEffect(() => {
    onMetaRef.current?.({ toCloneCount });
  }, [toCloneCount]);

  const fmt = (n: number, ccy: string) => `${ccy} ${n.toLocaleString()}`;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs text-gray-500 mb-1">Clone products from</p>
        <Select value={sourceId} onValueChange={onSourceChange}>
          <SelectTrigger>
            <SelectValue placeholder="Source region" />
          </SelectTrigger>
          <SelectContent>
            {(regions ?? [])
              .filter((r) => r.id !== targetRegionId)
              .map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {codeToFlag(r.code)} {r.display_name}
                  {r.is_fallback ? " · Fallback" : ""}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Rate status (+ manual fallback) */}
      {source && target && source.currency !== target.currency && (
        <div className="text-xs text-gray-500 space-y-1">
          {rate.isLoading ? (
            <p>Fetching exchange rate…</p>
          ) : apiRate != null ? (
            <p>
              Rate: 1 {source.currency} = {apiRate} {target.currency} · prices
              shown in {target.currency}
            </p>
          ) : (
            <div className="space-y-1">
              <p className="text-amber-600">
                Couldn't fetch the exchange rate — enter it manually.
              </p>
              <div className="flex items-center gap-2 text-gray-600">
                <span>1 {source.currency} =</span>
                <Input
                  type="number"
                  min={0}
                  className="h-8 w-28"
                  placeholder="rate"
                  value={manualRate}
                  onChange={(e) => setManualRate(e.target.value)}
                />
                <span>{target.currency}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk adjust (after conversion) */}
      {cloneRows.length > 0 && (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <p className="text-xs text-gray-500 mb-1">Adjust</p>
            <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INCREASE">Increase by</SelectItem>
                <SelectItem value="DECREASE">Decrease by</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-28">
            <p className="text-xs text-gray-500 mb-1">By</p>
            <Select value={valueType} onValueChange={(v) => setValueType(v as ValueType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PERCENT">Percent</SelectItem>
                <SelectItem value="AMOUNT">Amount</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-20">
            <p className="text-xs text-gray-500 mb-1">Value</p>
            <Input
              type="number"
              min={0}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <Button variant="outline" onClick={applyToAll}>
            Apply
          </Button>
        </div>
      )}

      <TooltipProvider>
        {products.map((p) => {
          const isExcluded = excluded.has(p.productId);
          return (
          <div
            key={p.productId}
            className={`rounded-md border border-gray-200 p-3 ${
              !p.skip && isExcluded ? "opacity-60" : ""
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {!p.skip && (
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-gray-900 cursor-pointer"
                    checked={!isExcluded}
                    onChange={() => toggleProduct(p.productId)}
                    aria-label={`Include ${p.product}`}
                  />
                )}
                <p className="text-sm font-medium">{p.product}</p>
              </div>
              {p.skip ? (
                <Badge variant="secondary">already in target</Badge>
              ) : isExcluded ? (
                <Badge variant="secondary">won't clone</Badge>
              ) : (
                <Badge variant="outline">will clone</Badge>
              )}
            </div>
            {!p.skip && (
              <div className="space-y-2">
                {p.offerings.map((o) => (
                  <div
                    key={o.standardId}
                    className="flex items-center gap-2 text-sm"
                  >
                    <span className="flex-1 truncate text-gray-600">
                      {o.name}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-gray-400 cursor-help">
                          <Info className="h-3.5 w-3.5" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Source: {fmt(o.sourceAmount, o.sourceCurrency)}
                        {rateValue != null && source?.currency !== target?.currency
                          ? ` · @ ${rateValue}`
                          : ""}
                      </TooltipContent>
                    </Tooltip>
                    <div className="flex items-center gap-1 w-40">
                      <span className="text-xs text-gray-400">
                        {target?.currency}
                      </span>
                      <Input
                        type="number"
                        min={0}
                        className="h-8"
                        value={expected[o.standardId] ?? ""}
                        onChange={(e) =>
                          setExpected((prev) => ({
                            ...prev,
                            [o.standardId]: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          );
        })}
      </TooltipProvider>
    </div>
  );
}
